import React, { useEffect, useState } from 'react';
import { Briefcase, MapPin, Send, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import JOBImage from '../assets/images/data/job.jpg'

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const skillsList = [
  // IT Skills
  'JavaScript', 'React', 'Node.js', 'Python', 'Django', 'Flutter',
  'HTML', 'CSS', 'Java', 'C++', 'SQL', 'MongoDB', 'AWS', 'Docker',
  'Kubernetes', 'Linux', 'Git', 'TypeScript', 'GraphQL', 'REST APIs',
  'Azure', 'Firebase', 'CI/CD', 'Agile Methodology', 'JIRA',

  // Non-IT Skills
  'Communication', 'Teamwork', 'Leadership', 'Time Management',
  'Problem Solving', 'Critical Thinking', 'Project Management',
  'Creativity', 'Adaptability', 'Emotional Intelligence',
  'Customer Service', 'Sales', 'Marketing', 'Accounting',
  'Data Entry', 'Public Speaking', 'Writing', 'Negotiation',
  'Conflict Resolution', 'Decision Making', 'Tally', 'GST', 'Billing',
  'MS Office', 'Excel', 'PowerPoint', 'Word', 'Bookkeeping',
  'Inventory Management', 'Office Administration', 'Clerical Work',
  'Payroll Processing', 'Bank Reconciliation', 'Business Communication',
  'Purchase Order Management', 'Invoicing', 'Vendor Management',

  // Transportation & Logistics Skills
  'Fleet Management', 'Logistics Coordination', 'Warehouse Management',
  'Supply Chain Management', 'Route Planning', 'Dispatching',
  'Transport Compliance', 'Freight Handling', 'Import/Export Documentation',
  'GPS Tracking Systems', 'Load Optimization', 'Shipping & Receiving',
  'Inventory Control', 'Material Handling', 'Customs Clearance',
  'Logistics Software (e.g., SAP, Oracle TMS)', 'Order Fulfillment',
  'Packaging and Labeling', 'Vehicle Maintenance Scheduling',
  'Traffic Management', 'Delivery Scheduling', 'Warehouse Safety Protocols'
];


const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [formStep, setFormStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const validateStep1 = () => {
    const errors = {};
    if (!formData.name) errors.name = "Full Name is required";
    if (!formData.email) errors.email = "Email is required";
    if (!formData.number) errors.number = "Phone Number is required";
    if (!formData.resume_file) errors.resume_file = "Resume is required";
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    if (!formData.experience_years) errors.experience_years = "Experience is required";
    if (!formData.current_jobtitle) errors.current_jobtitle = "Current Job Title is required";
    if (!formData.skills) errors.skills = "Skills are required";
    if (!formData.current_salary) errors.current_salary = "Current Salary is required";
    if (!formData.expected_salary) errors.expected_salary = "Expected Salary is required";
    if (formData.willing_to_relocate === undefined || formData.willing_to_relocate === '')
      errors.willing_to_relocate = "Please select relocate option";
    if (formData.authorized_to_work === undefined || formData.authorized_to_work === '')
      errors.authorized_to_work = "Please select work authorization";
    return errors;
  };

  useEffect(() => {
    axios.get(`${API_URL}jobs/active`)
      .then(res => {
        setJobs(res.data);
        setLoading(false);
      })
      .catch(err => {
        setError('No jobs found.');
        setLoading(false);
      });
  }, []);

  const handleCardClick = async (jobId) => {
    try {
      const res = await axios.get(`${API_URL}jobs/single/${jobId}`);
      setSelectedJob(res.data);
    } catch (err) {
      console.error('Failed to fetch job details');
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setTimeout(() => {
      setFormStep(1);
      setFormErrors({});
      setFormSubmitted(false); // delay reset until modal fully closes
      setFormData({});
      setSelectedJob(null); // Optional: clear job
    }, 300); // Slight delay allows UI to display briefly
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const stepErrors = validateStep2(); // Final validation
    if (Object.keys(stepErrors).length > 0) {
      setFormErrors(stepErrors);
      return;
    }

    setIsSubmitting(true);

    const applicationData = {
      job_id: selectedJob.id,
      basicInformation: {
        name: formData.name,
        email: formData.email,
        number: formData.number,
        location: selectedJob.location || 'N/A',
      },
      jobRelatedInformation: {
        cover_letter: formData.cover_letter,
        portfolio_link: formData.portfolio_link,
        linkedin_profile: formData.linkedin_profile,
        github_profile: formData.github_profile
      },
      professionalInformation: {
        experience_years: formData.experience_years,
        current_jobtitle: formData.current_jobtitle,
        skills: (formData.skills || '').split(',').map(s => s.trim()).filter(Boolean)
      },
      logistics: {
        current_salary: formData.current_salary,
        expected_salary: formData.expected_salary,
        willing_to_relocate: formData.willing_to_relocate,
        authorized_to_work: formData.authorized_to_work
      }
    };

    const data = new FormData();
    data.append('data', JSON.stringify(applicationData));
    data.append('resume_file', formData.resume_file); // resume file stays outside

    try {
      await axios.post(`${API_URL}job-applications/apply`, data);
      Swal.fire({
        icon: 'success',
        title: 'Thank you for applying!',
        text: 'We’ve received your application and will be in touch soon.',
        confirmButtonColor: '#facc15'
      }).then(() => closeModal());

      setFormErrors({});
      setFormSubmitted(true);
    } catch (err) {
      console.error('Submission error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to submit your application. Please try again later!',
        confirmButtonColor: '#f87171'
      });
    } finally {
      setIsSubmitting(false); // 🔄 Stop loading
    }
  };

  return (
    <main className="bg-white text-gray-800 overflow-hidden relative font-sans">
      <Helmet>
        <title>Careers at S Infinite Express | Join Our Team</title>
        <meta
          name="description"
          content="Explore exciting career opportunities at S Infinite Express. Join India's fastest-growing logistics company and build your future in transportation, supply chain, and freight services."
        />
        <meta
          name="keywords"
          content="Logistics jobs, S Infinite careers, logistics company hiring, job openings India, freight jobs, transportation jobs, supply chain jobs, warehouse jobs, delivery jobs, apply logistics India, career in courier company, jobs in logistics sector"
        />
        <meta name="author" content="S Infinite Express" />
        <meta property="og:title" content="Careers at S Infinite Express" />
        <meta
          property="og:description"
          content="Be part of our fast-growing logistics team. Discover job openings in transportation, warehousing, delivery, and operations at S Infinite Express."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinfiniteexpress.com/careers" />
        <link rel="canonical" href="https://sinfiniteexpress.com/careers" />
      </Helmet>

      <div className="min-h-screen bg-white px-4 sm:px-6 lg:px-12 py-12">
        <div className="max-w-7xl mx-auto space-y-32">

          {/* Hero Section */}
          <section className="bg-gradient-to-tr from-yellow-100 via-white to-blue-50 rounded-xl shadow-md py-20 px-6 sm:px-10 lg:px-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <h1 className="text-5xl font-extrabold text-[#211f57] leading-tight">Shape the Future of Logistics</h1>
                <p className="mt-4 text-lg text-gray-700 max-w-xl">
                  Join <span className="text-red-600 font-semibold">S Infinite Express</span> and be part of a team that’s
                  reimagining delivery, transportation, and customer experiences across India.
                </p>
                <a href="#openings" className="inline-block mt-6 bg-[#fff44f] text-[#211f57] font-bold px-6 py-3 rounded-full shadow hover:scale-105 transition">Explore Openings</a>
              </div>
              <div className="hidden md:block">
                <img src={JOBImage} alt="Careers at S Infinite Express" className="w-full rounded-xl shadow-lg" />
              </div>
            </div>
          </section>

          {/* Current Openings */}
          {!selectedJob && (
            <section id="openings" className="scroll-mt-24">
              <h2 className="text-4xl font-bold text-center text-blue-800 mb-12">Open Roles at S Infinite Express</h2>

              {loading && <p className="text-center text-gray-600">Loading jobs...</p>}
              {error && <p className="text-center text-red-500">{error}</p>}

              {jobs.length === 0 && !loading && !error ? (
                <p className="text-center text-gray-600 text-lg mt-4">
                  Currently, there are no openings available.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
                  {jobs.map((job) => (
                    <motion.div
                      key={job.id}
                      initial="hidden"
                      whileInView="show"
                      viewport={{ once: true }}
                      variants={fadeInUp}
                      onClick={() => handleCardClick(job.id)}
                      className="cursor-pointer bg-white p-6 rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    >
                      <h3 className="text-xl font-bold text-[#211f57] flex items-center">
                        <Briefcase className="w-5 h-5 mr-2 text-yellow-500" />
                        {job.title}
                      </h3>

                      <div className="flex flex-wrap text-sm text-gray-500 gap-4 mt-3">
                        <span className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" /> {job.location}
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" /> {job.employment_type}
                        </span>
                        <span className="flex items-center">
                          <Send className="w-4 h-4 mr-1" /> {new Date(job.updated_at).toDateString()}
                        </span>
                        <span className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-1" /> {job.workplace_type}
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 mt-4 space-y-2">
                        <p><span className="font-medium text-gray-800">Experience:</span> {job.experience}</p>
                        <p><span className="font-medium text-gray-800">Department:</span> {job.industry}</p>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedJob(job);
                          setShowForm(true);
                        }}
                        className="mt-4 w-full bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 text-[#211f57] font-semibold px-5 py-2 rounded-xl hover:scale-105 transition-transform"
                      >
                        Apply Now
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>
          )}

          {selectedJob && !showForm && (
            <>
              <div className="mt-10 max-w-5xl mx-auto bg-white rounded-3xl shadow-lg p-8 space-y-6">
                {/* Header */}
                <div className="flex justify-between items-start flex-wrap gap-4">
                  <div>
                    <h2 className="text-3xl font-extrabold text-[#211f57]">{selectedJob.title}</h2>
                    {/* <p className="text-gray-600 text-sm mt-1">{selectedJob.job_code}</p> */}
                    <div className="mt-2 text-gray-700 space-y-2">
                      {selectedJob.description.split('\n').map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>

                  </div>
                  <button
                    onClick={() => setShowForm(true)}
                    className="px-6 py-2 bg-yellow-400 text-[#211f57] font-semibold rounded-xl hover:scale-105 transition-transform">
                    Apply Now
                  </button>
                </div>

                {/* Info Grid */}
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 text-sm text-gray-700">
                  <p><strong>Company:</strong> {selectedJob.company}</p>
                  <p><strong>Location:</strong> {selectedJob.location}</p>
                  <p><strong>Experience:</strong> {selectedJob.experience}</p>
                  <p><strong>Salary:</strong> {selectedJob.salary}</p>
                  <p><strong>Level:</strong> {selectedJob.job_level}</p>
                  <p><strong>Type:</strong> {selectedJob.employment_type}</p>
                  <p><strong>Industry:</strong> {selectedJob.industry}</p>
                  <p><strong>Workplace:</strong> {selectedJob.workplace_type}</p>
                  <p><strong>Posted:</strong> {new Date(selectedJob.updated_at).toDateString()}</p>
                </div>

                {/* Tags */}
                <div className="grid sm:grid-cols-2 gap-6">
                  {/* Skills */}
                  <div>
                    <h4 className="text-[#211f57] font-bold mb-2">Skills Required:</h4>
                    <ul className="flex flex-wrap gap-2">
                      {selectedJob.skills.map((skill, i) => (
                        <li key={i} className="bg-yellow-100 text-sm px-3 py-1 rounded-full text-[#211f57] font-medium">
                          {skill}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Languages */}
                  <div>
                    <h4 className="text-[#211f57] font-bold mb-2">Languages:</h4>
                    <ul className="flex flex-wrap gap-2">
                      {selectedJob.languages.map((lang, i) => (
                        <li key={i} className="bg-blue-100 text-sm px-3 py-1 rounded-full text-[#211f57] font-medium">
                          {lang}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <h4 className="text-[#211f57] font-bold mb-2">Requirements:</h4>
                  <ul className="list-disc list-inside text-gray-700">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>

                {/* Responsibilities */}
                <div>
                  <h4 className="text-[#211f57] font-bold mb-2">Responsibilities:</h4>
                  <ul className="list-disc list-inside text-gray-700">
                    {selectedJob.responsibilities.map((res, i) => (
                      <li key={i}>{res}</li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div>
                  <h4 className="text-[#211f57] font-bold mb-2">Benefits:</h4>
                  <ul className="flex flex-wrap gap-2">
                    {selectedJob.benefits.map((benefit, i) => (
                      <li key={i} className="bg-green-100 px-3 py-1 rounded-full text-sm text-[#211f57] font-medium">
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {/* Back Button */}
              <div className="text-center mt-6">
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-sm text-blue-700 underline hover:text-blue-900 transition"
                >
                  ← Back to Current Openings
                </button>
              </div>
            </>
          )}

          {showForm && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex justify-center items-start sm:items-center px-2 py-6 overflow-y-auto">
              <div className="bg-white rounded-2xl w-full max-w-2xl p-4 sm:p-6 md:p-8 shadow-2xl relative">

                <button onClick={closeModal} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 transition">
                  <X className="w-5 h-5" />
                </button>
                {!formSubmitted ? (
                  <>
                    <h3 className="text-2xl font-bold text-center text-[#211f57] mb-1">
                      Apply for <span className="text-red-600">{selectedJob?.title}</span>
                    </h3>

                    <form className="space-y-6 mt-6" onSubmit={handleSubmit}>
                      {/* Step Indicator */}
                      <div className="flex justify-center gap-2 mb-6">
                        {[1, 2].map((num) => (
                          <span key={num}
                            className={`w-3 h-3 rounded-full ${formStep === num ? 'bg-yellow-500' : 'bg-gray-300'}`} />
                        ))}
                      </div>

                      {/* Step 1: Personal + Job Details */}
                      {formStep === 1 && (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                              <input
                                type="text"
                                value={formData.name || ''}
                                onChange={e => {
                                  setFormData({ ...formData, name: e.target.value });
                                  setFormErrors(prev => ({ ...prev, name: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.name && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input
                                type="email"
                                value={formData.email || ''}
                                onChange={e => {
                                  setFormData({ ...formData, email: e.target.value });
                                  setFormErrors(prev => ({ ...prev, email: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.email && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
                              )}
                            </div>
                          </div>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                              <input
                                type="text"
                                value={formData.number || ''}
                                onChange={e => {
                                  setFormData({ ...formData, number: e.target.value });
                                  setFormErrors(prev => ({ ...prev, number: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.number && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.number}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile</label>
                              <input type="url" value={formData.linkedin_profile || ''}
                                onChange={e => setFormData({ ...formData, linkedin_profile: e.target.value })}
                                className="w-full border rounded px-4 py-2" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Resume</label>
                            <input
                              type="file"
                              onChange={e => {
                                setFormData({ ...formData, resume_file: e.target.files[0] });
                                setFormErrors(prev => ({ ...prev, resume_file: '' }));
                              }}
                              className="w-full"
                            />
                            {formErrors.resume_file && (
                              <p className="text-sm text-red-500 mt-1">{formErrors.resume_file}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
                            <textarea value={formData.cover_letter || ''}
                              onChange={e => setFormData({ ...formData, cover_letter: e.target.value })}
                              className="w-full border rounded px-4 py-2" rows={4} />
                          </div>

                        </div>
                      )}

                      {/* Step 2: Professional Info */}
                      {formStep === 2 && (
                        <div className="space-y-4">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                              <input
                                type="number"
                                value={formData.experience_years || ''}
                                onChange={e => {
                                  setFormData({ ...formData, experience_years: e.target.value });
                                  setFormErrors(prev => ({ ...prev, experience_years: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.experience_years && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.experience_years}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Current Job Title</label>
                              <input
                                type="text"
                                value={formData.current_jobtitle || ''}
                                onChange={e => {
                                  setFormData({ ...formData, current_jobtitle: e.target.value });
                                  setFormErrors(prev => ({ ...prev, current_jobtitle: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.current_jobtitle && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.current_jobtitle}</p>
                              )}
                            </div>
                          </div>

                          <div className="relative">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Skills (comma-separated)</label>
                            <input
                              type="text"
                              value={formData.skills || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const lastTerm = value.split(',').pop().trim().toLowerCase();

                                setFormData({ ...formData, skills: value });
                                setFormErrors(prev => ({ ...prev, skills: '' }));

                                if (lastTerm.length > 0) {
                                  const filtered = skillsList.filter(
                                    (skill) =>
                                      skill.toLowerCase().startsWith(lastTerm) &&
                                      !value.toLowerCase().split(',').map((s) => s.trim()).includes(skill.toLowerCase())
                                  );
                                  setSuggestions(filtered);
                                } else {
                                  setSuggestions([]);
                                }
                              }}
                              placeholder="e.g. Tally, React, Node.js"
                              className="w-full border rounded px-4 py-2"
                            />
                            {formErrors.skills && (
                              <p className="text-sm text-red-500 mt-1">{formErrors.skills}</p>
                            )}

                            {suggestions.length > 0 && (
                              <ul className="absolute z-10 w-full bg-white border mt-1 rounded shadow-md max-h-40 overflow-y-auto">
                                {suggestions.map((skill, index) => (
                                  <li
                                    key={index}
                                    onClick={() => {
                                      const currentValue = formData.skills || '';
                                      const parts = currentValue.split(',');
                                      parts[parts.length - 1] = skill; // replace last typed term
                                      const updated = parts.map(s => s.trim()).filter(Boolean);
                                      setFormData({ ...formData, skills: updated.join(', ') });
                                      setSuggestions([]);
                                    }}
                                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                                  >
                                    {skill}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Current Salary (₹)</label>
                              <input
                                type="number"
                                value={formData.current_salary || ''}
                                onChange={e => {
                                  setFormData({ ...formData, current_salary: e.target.value });
                                  setFormErrors(prev => ({ ...prev, current_salary: '' }));
                                }}
                                className="w-full border rounded px-4 py-2"
                              />
                              {formErrors.current_salary && (
                                <p className="text-sm text-red-500 mt-1">{formErrors.current_salary}</p>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Salary (₹)</label>
                              <input type="number" value={formData.expected_salary || ''}
                                onChange={e => setFormData({ ...formData, expected_salary: e.target.value })}
                                className="w-full border rounded px-4 py-2" />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Willing to Relocate?</label>
                              <select value={formData.willing_to_relocate ?? ''} onChange={e =>
                                setFormData({ ...formData, willing_to_relocate: e.target.value === 'true' })}
                                className="w-full border rounded px-4 py-2" required>
                                <option value="">Select</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Authorized to Work?</label>
                              <select value={formData.authorized_to_work ?? ''} onChange={e =>
                                setFormData({ ...formData, authorized_to_work: e.target.value === 'true' })}
                                className="w-full border rounded px-4 py-2" required>
                                <option value="">Select</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex justify-between items-center pt-6">
                        {formStep > 1 ? (
                          <button type="button"
                            onClick={() => setFormStep(formStep - 1)}
                            className="text-sm px-4 py-2 border border-gray-300 rounded hover:bg-gray-100">
                            Back
                          </button>
                        ) : <div />}

                        {formStep < 2 ? (
                          <button
                            type="button"
                            onClick={() => {
                              const stepErrors = formStep === 1 ? validateStep1() : validateStep2();
                              if (Object.keys(stepErrors).length > 0) {
                                setFormErrors(stepErrors);
                              } else {
                                setFormErrors({});
                                setFormStep(formStep + 1);
                              }
                            }}
                            className="bg-yellow-400 text-[#211f57] font-semibold px-6 py-2.5 rounded-lg hover:scale-[1.02] transition-all"
                          >
                            Next
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`bg-yellow-400 text-[#211f57] font-semibold px-6 py-2.5 rounded-lg transition-all ${isSubmitting ? 'opacity-60 cursor-not-allowed' : 'hover:scale-[1.02]'
                              }`}
                          >
                            {isSubmitting ? (
                              <div className="flex items-center gap-2">
                                <svg className="animate-spin h-5 w-5 text-[#211f57]" viewBox="0 0 24 24" fill="none">
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                  ></path>
                                </svg>
                                Submitting...
                              </div>
                            ) : (
                              'Submit Application'
                            )}
                          </button>
                        )}
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-12 px-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mx-auto mb-6">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>

                    <h2 className="text-3xl font-bold text-[#211f57] mb-2">Thank you for applying!</h2>
                    <p className="text-lg text-gray-600">We’ve received your application and will be in touch soon.</p>

                    <button
                      onClick={closeModal}
                      className="mt-8 px-6 py-2.5 bg-yellow-400 text-[#211f57] font-semibold rounded-full shadow hover:shadow-lg hover:scale-105 transition-all"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default CareersPage;